import { Navigate } from 'react-router-dom'
import { ReactElement, useEffect, useState } from 'react'
import { useTimeout } from 'usehooks-ts'

import { useFeatureFlagsReady } from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag/useFeatureFlagsReady'
import { useActivePostHogFeatureFlags } from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag/usePostHogFeatureFlag'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions'
import { useAuthState } from '@percent/admin-dashboard/common/hooks'
import { Spinner } from '@percent/lemonade'
import { ROUTE_GUARD_WAIT_MS } from '@percent/admin-dashboard/constants/routeGuardTiming'
import { DEFAULT_ROUTE } from './default-route'
import { AccessRouteProps } from './AccessRoute.types'

export function AccessRoute({
  legacyPermissionFlag,
  permissionEnabledFlag,
  permissions,
  redirectTo = DEFAULT_ROUTE,
  children
}: AccessRouteProps): ReactElement {
  const isReady = useFeatureFlagsReady()
  const activeFeatureFlags = useActivePostHogFeatureFlags()
  const { permissionsSet } = useAuthState()
  const { userHasPermissions } = usePermissions()

  const legacyFlagValue = legacyPermissionFlag ? activeFeatureFlags?.includes(legacyPermissionFlag) ?? false : undefined
  const permissionEnabledFlagValue = permissionEnabledFlag
    ? activeFeatureFlags?.includes(permissionEnabledFlag) ?? false
    : undefined

  const [loaded, setLoaded] = useState(false)
  const [haveWaited, setHaveWaited] = useState(false)

  const isPermissionOnly = !permissionEnabledFlag && !legacyPermissionFlag && Boolean(permissions)
  const routeRequiresPermissions = Boolean(permissionEnabledFlagValue) || isPermissionOnly

  const flagsReady = isPermissionOnly || isReady

  useEffect(() => {
    if (permissionsSet && flagsReady && !loaded) {
      setLoaded(true)
    }
  }, [flagsReady, loaded, permissionsSet])

  useTimeout(() => {
    setHaveWaited(true)
  }, ROUTE_GUARD_WAIT_MS)

  useEffect(() => {
    if (!loaded && haveWaited && flagsReady && (!routeRequiresPermissions || permissionsSet)) {
      setLoaded(true)
    }
  }, [haveWaited, flagsReady, loaded, routeRequiresPermissions, permissionsSet])

  if (!loaded) {
    return <Spinner size="m" data-testid="spinner" />
  }

  let hasAccess = true

  if (permissionEnabledFlagValue) {
    hasAccess = permissions ? userHasPermissions(permissions) : true
  } else if (!permissionEnabledFlag && !legacyPermissionFlag && permissions) {
    hasAccess = userHasPermissions(permissions)
  } else {
    hasAccess = legacyPermissionFlag ? Boolean(legacyFlagValue) : true
  }

  if (!hasAccess) {
    // TODO: Replace redirect with AccessDeniedPage component
    return <Navigate data-testid="redirect" to={redirectTo} replace />
  }

  return children as ReactElement
}
